/**
 * Everything WhatsApp says about a piece of wording, and what an owner can DO about each of
 * those things — as pure functions, so the whole vocabulary is testable without mounting a
 * screen.
 *
 * `template-draft.js` is the other half: that one is about writing a template, this one is about
 * reading one back. They are deliberately separate files because they fail differently — a bug
 * in the draft is a refusal from Meta, a bug in here is a screen that tells an owner the wrong
 * thing about a template that is already live.
 *
 * ── THE RULE THIS FILE INHERITS ─────────────────────────────────────────────────────
 *
 * A STATUS, A CATEGORY OR A QUALITY RATING IS REPORTED, NEVER DERIVED. Every list below is a
 * list of values we have a SENTENCE for — not a list of values that are allowed. A value Meta
 * sends that is not in one of them is displayed AS META WROTE IT, never folded into the nearest
 * one we recognise and never silently dropped. Meta's own reference pages disagree with each
 * other about these enums today (`ARCHIVED` is on the edge page and missing from the node page;
 * `INCORRECT_CATEGORY` is a webhook reason and not a template field value; `FLAGGED`, `LOCKED`
 * and `REINSTATED` are webhook events Meta defines nowhere), so a closed list would be wrong the
 * day it shipped.
 *
 * ── WHAT IS NOT HERE, ON PURPOSE ────────────────────────────────────────────────────
 *
 * `quality_score.reasons`. It does not exist. Meta's quality-score object is `{ score, date }`
 * and nothing else on any page it publishes; the four "reasons" that circulate (spam, didn't
 * sign up, no longer needed, offensive) come from third-party blogs describing a hover state in
 * Meta's own Manager. Reading a field Meta does not send would render an empty list under a
 * heading that promises an explanation.
 */

/**
 * The statuses we have words for. Meta's API enum, as its template node and its
 * `message_templates` edge publish it between them.
 *
 * `APPEAL_REQUESTED` IS ABSENT AND THAT IS CORRECT: it is a label in WhatsApp Manager's own UI,
 * not an API value — the API says `IN_APPEAL` for the same state. It is listed in
 * `MANAGER_ALIASES` so that if it ever does arrive on the wire it reads as the state it means
 * rather than as a foreign word.
 */
export const KNOWN_STATUSES = [
  'APPROVED',
  'PENDING',
  'IN_APPEAL',
  'PAUSED',
  'DISABLED',
  'REJECTED',
  'ARCHIVED',
  'PENDING_DELETION',
  'DELETED',
  'LIMIT_EXCEEDED',
];

/** What WhatsApp Manager calls a state that the API names differently. */
export const MANAGER_ALIASES = { APPEAL_REQUESTED: 'IN_APPEAL' };

/** Meta's four quality ratings, and the words WhatsApp Manager shows for them. */
export const QUALITY_SCORES = {
  GREEN: 'HIGH',
  YELLOW: 'MEDIUM',
  RED: 'LOW',
  UNKNOWN: 'UNKNOWN',
};

/**
 * The categories we have words for. The first three are the only ones that can be CREATED;
 * `FREE_SERVICE` and the long legacy list (`ACCOUNT_UPDATE`, `SHIPPING_UPDATE`, `OTP`, …) are
 * read-only values Meta still returns on old templates, and an owner who has one must see what
 * it says rather than a blank.
 */
export const KNOWN_CATEGORIES = [
  'UTILITY',
  'MARKETING',
  'AUTHENTICATION',
  'FREE_SERVICE',
];

/** Meta's three documented sub-categories. */
export const KNOWN_SUB_CATEGORIES = [
  'ORDER_DETAILS',
  'ORDER_STATUS',
  'RICH_ORDER_STATUS',
];

/**
 * Refusal reasons we have words for. The first five are the template field's own enum; the last
 * two are `message_template_status_update` webhook reasons, included because they describe the
 * same refusal and an owner may well be looking at a template that carries one.
 */
export const KNOWN_REASONS = [
  'ABUSIVE_CONTENT',
  'INVALID_FORMAT',
  'PROMOTIONAL',
  'SCAM',
  'TAG_CONTENT_MISMATCH',
  'INCORRECT_CATEGORY',
  'CATEGORY_NOT_AVAILABLE',
];

/**
 * Every button type we have a word for — far more than the four this product AUTHORS
 * (`template-draft.js`), because a business that built a template in Meta's own Manager must
 * still be able to read it here. A type outside this list is shown as Meta wrote it.
 */
export const KNOWN_BUTTON_TYPES = [
  'QUICK_REPLY',
  'URL',
  'PHONE_NUMBER',
  'COPY_CODE',
  'OTP',
  'FLOW',
  'MPM',
  'SPM',
  'CATALOG',
  'VOICE_CALL',
  'ORDER_DETAILS',
  'APP',
  'REMINDER',
  'POSTBACK',
  'EXTENSION',
  'SEND_LOCATION',
];

/** Header formats Meta documents, including the two missing from its own field enum. */
export const KNOWN_HEADER_FORMATS = [
  'TEXT',
  'IMAGE',
  'VIDEO',
  'GIF',
  'DOCUMENT',
  'LOCATION',
  'PRODUCT',
];

const upper = value =>
  typeof value === 'string' ? value.trim().toUpperCase() : '';

/** Meta's own word, folded for comparison, with a Manager-only label mapped to its API state. */
export const statusOf = template => {
  const raw = upper(template?.status);
  return MANAGER_ALIASES[raw] ?? raw;
};

export const isKnownStatus = status =>
  KNOWN_STATUSES.includes(statusOf({ status }));

/**
 * Only an APPROVED template can start a conversation. `ARCHIVED` is deliberately NOT sendable
 * even though an archived template was approved before it was archived — Meta: "Archived
 * templates cannot be sent in template messages."
 */
export const isSendable = template => statusOf(template) === 'APPROVED';

/** Statuses where the template is out of action and only WhatsApp can change that. */
const STOPPED = [
  'REJECTED',
  'PAUSED',
  'DISABLED',
  'ARCHIVED',
  'LIMIT_EXCEEDED',
  'PENDING_DELETION',
  'DELETED',
];

/**
 * Worth surfacing at the top of a list of hundreds. Two different things count: a template that
 * cannot be sent at all, and one that CAN be sent but is on its way to being paused — which is
 * the entire point of reading `quality_score` in the first place. WhatsApp pauses a template
 * that reaches its lowest rating for 3 hours, then 6, then disables it permanently; a YELLOW
 * rating is the only warning that arrives before the first of those.
 */
export const needsAttention = template => {
  if (STOPPED.includes(statusOf(template))) return true;
  const score = upper(template?.quality?.score);
  return score === 'RED' || score === 'YELLOW';
};

/**
 * Meta: "Only templates with an APPROVED, REJECTED, or PAUSED status can be edited."
 * Everything else — in review, under appeal, disabled, archived, on its way out — is refused at
 * Graph, so the screen must not offer the door.
 */
export const canEdit = template =>
  ['APPROVED', 'REJECTED', 'PAUSED'].includes(statusOf(template));

/**
 * Meta: "You cannot edit the category of an approved template." So the category is changeable
 * only while the template is refused or paused — which is exactly when an owner wants to change
 * it, because a wrong category is one of Meta's own refusal reasons.
 */
export const canChangeCategory = template =>
  ['REJECTED', 'PAUSED'].includes(statusOf(template));

/** Meta: "Templates that are in a disabled status cannot be deleted." */
export const canDelete = template =>
  !['DISABLED', 'DELETED', 'PENDING_DELETION'].includes(statusOf(template));

export const canUnpause = template => statusOf(template) === 'PAUSED';

/** The four button types `template-draft.js` can write, restated here for the gate. */
export const EDITABLE_BUTTON_TYPES = [
  'QUICK_REPLY',
  'URL',
  'PHONE_NUMBER',
  'COPY_CODE',
];

/**
 * A sign-in code template is rebuilt from two flags, so its ONE copy-code sign-in button survives.
 * A one-tap or zero-tap one does not: those carry an Android package name and an app signing hash
 * that nothing here reads, and rebuilding the button as a copy-code one would silently downgrade
 * how the customer receives her code.
 */
const blockingButtons = (component, authentication) => {
  const list = Array.isArray(component?.buttons) ? component.buttons : [];
  return list.flatMap(button => {
    const type = upper(button?.type);
    if (authentication) {
      if (type === 'OTP' && upper(button.otp_type) === 'COPY_CODE') return [];
      return [{ reason: 'BUTTON', detail: type }];
    }
    return EDITABLE_BUTTON_TYPES.includes(type)
      ? []
      : [{ reason: 'BUTTON', detail: type }];
  });
};

/**
 * ── THE EDIT GATE, AND THE BUG IT EXISTS BECAUSE OF ─────────────────────────────────
 *
 * Meta's edit endpoint REPLACES ALL COMPONENTS with whatever the request carries. This editor can
 * read back a TEXT header, a body, a footer and four kinds of button — and NOTHING ELSE. So for
 * any template built out of anything else, opening the form and pressing save does not edit the
 * template: it DELETES the part the form could not hold.
 *
 * That is not hypothetical. `templateToDraft` read a header as `header.text`, which is `''` for an
 * IMAGE header; the wire then carried `header: ''`, the builder emitted no header component, and
 * Meta replaced a live approved template's picture with nothing — and sent it back into review.
 * The facts panel one click earlier said, correctly, "a picture". A carousel and a limited-time
 * offer went the same way, and so did a FLOW button, whose `flow_id` no draft row can carry.
 *
 * The rule this encodes: REFUSE TO EDIT WHAT WE CANNOT ROUND-TRIP. A disabled button with a
 * sentence naming the part beats a save that destroys her template, every time — and WhatsApp
 * Manager can edit all of these today.
 *
 * @returns {{reason: string, detail: string}[]} empty when the whole template survives a round
 *   trip through the form. `reason` is a sentence key; `detail` is Meta's own word for the part.
 */
export const editBlockers = template => {
  const parts = Array.isArray(template?.components) ? template.components : [];
  const authentication = upper(template?.category) === 'AUTHENTICATION';
  const found = [];
  parts.forEach(part => {
    const type = upper(part?.type);
    if (type === 'HEADER') {
      const format = upper(part.format) || 'TEXT';
      // An authentication template's wording is Meta's; a header on one is not something this
      // form models at all, so it would be dropped whatever its format.
      if (format !== 'TEXT' || authentication) {
        found.push({ reason: 'MEDIA_HEADER', detail: format });
      }
      return;
    }
    if (type === 'CAROUSEL') {
      found.push({ reason: 'CAROUSEL', detail: '' });
      return;
    }
    if (type === 'LIMITED_TIME_OFFER') {
      found.push({ reason: 'OFFER', detail: '' });
      return;
    }
    if (type === 'BUTTONS') {
      found.push(...blockingButtons(part, authentication));
      return;
    }
    if (type === 'BODY') {
      // THE WORDING IS A PART TOO, and the one nobody thinks of as one. A sign-in code message has
      // no wording of its own — WhatsApp writes it — so a body with text could only be moving INTO
      // that shape, where it is discarded; and a body without text is already that shape, which the
      // ordinary branch would rebuild as an empty message WhatsApp refuses.
      const wording = typeof part.text === 'string' ? part.text.trim() : '';
      if (authentication === (wording !== '')) {
        found.push({ reason: 'WORDING', detail: '' });
      }
      return;
    }
    if (type !== 'FOOTER') {
      found.push({ reason: 'PART', detail: type });
    }
  });
  return found.filter(
    (one, index) =>
      found.findIndex(
        other => other.reason === one.reason && other.detail === one.detail
      ) === index
  );
};

/**
 * Both halves of the answer to "may she press Edit": Meta's status rule AND whether this form can
 * hold the whole template. Every caller wants both, and a caller that checked only the first is
 * the bug above.
 */
export const canEditHere = template =>
  canEdit(template) && editBlockers(template).length === 0;

/**
 * An appeal is WhatsApp Manager only — Meta publishes no appeal API, for a refusal or for a
 * re-categorisation ("a review can only be requested via WhatsApp Manager"). So the screen
 * offers the DOOR to Meta's own tool rather than a button that cannot work.
 */
export const canAppeal = template =>
  ['REJECTED', 'PAUSED', 'DISABLED'].includes(statusOf(template));

/**
 * Meta's own template manager, for the things only it can do: appeal a refusal, ask for a
 * category review, unarchive a template inside its 28-day window.
 *
 * The WABA id is what identifies the business's template library there. This is a NAVIGATION
 * convenience and is treated as one: if Meta reshapes the path, the owner still lands in her own
 * Business Manager rather than nowhere, and nothing on our screen depends on it.
 */
export const managerUrl = wabaId =>
  wabaId
    ? `https://business.facebook.com/wa/manage/message-templates/?waba_id=${encodeURIComponent(wabaId)}`
    : 'https://business.facebook.com/wa/manage/message-templates/';

/**
 * How long WhatsApp keeps retrying, as a unit and a number rather than a sentence — the
 * sentence is i18n's business, in whichever language the owner reads.
 *
 * `-1` is Meta's shorthand for "as long as it can", which is 30 days.
 */
export const describeTtl = seconds => {
  if (seconds === null || seconds === undefined || seconds === '') return null;
  const value = Number(seconds);
  if (!Number.isFinite(value)) return null;
  if (value === -1) return { unit: 'MAX', value: 30 };
  if (value < 3600) return { unit: 'MINUTES', value: Math.round(value / 60) };
  if (value < 86400) return { unit: 'HOURS', value: Math.round(value / 3600) };
  return { unit: 'DAYS', value: Math.round(value / 86400) };
};

const typeOf = part => upper(part?.type);

const numberOrNull = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const readHeader = header => {
  const format = upper(header.format) || 'TEXT';
  return {
    format: format,
    known: KNOWN_HEADER_FORMATS.includes(format),
    text: typeof header.text === 'string' ? header.text : '',
    // A media header carries an uploaded asset handle instead of text. Its presence is the only
    // honest thing to report: the handle is not a URL and cannot be shown.
    hasMedia: Boolean(header.example?.header_handle?.length),
  };
};

const readButtons = component => {
  const list = Array.isArray(component?.buttons) ? component.buttons : [];
  return list.map(button => {
    const type = typeOf(button);
    return {
      type: type,
      known: KNOWN_BUTTON_TYPES.includes(type),
      text: typeof button.text === 'string' ? button.text : '',
      url: typeof button.url === 'string' ? button.url : '',
      phoneNumber:
        typeof button.phone_number === 'string' ? button.phone_number : '',
      // COPY_CODE's example is a bare string where every other example on a template is an
      // array. Both shapes are read, because a template authored elsewhere may carry either.
      code: Array.isArray(button.example)
        ? String(button.example[0] ?? '')
        : String(button.example ?? ''),
      otpType: upper(button.otp_type),
      flow: button.flow_name ?? button.flow_id ?? '',
    };
  });
};

/**
 * One template's components, read rather than guessed — and case-folded, because Meta's own
 * examples spell the same component `"BODY"` on one page and `"body"` on another, and a
 * template authored through a different tool can arrive either way. A comparison that assumed
 * upper case would render a real template as having no message at all.
 */
export const readComponents = template => {
  const parts = Array.isArray(template?.components) ? template.components : [];
  const find = wanted => parts.find(part => typeOf(part) === wanted);
  const header = find('HEADER');
  const body = find('BODY');
  const footer = find('FOOTER');
  const buttons = find('BUTTONS');
  const carousel = find('CAROUSEL');
  const offer = find('LIMITED_TIME_OFFER');
  return {
    header: header ? readHeader(header) : null,
    body: {
      text: typeof body?.text === 'string' ? body.text : '',
      // An authentication template has NO body text: Meta writes the wording itself and this
      // flag is the owner's only say in it.
      securityRecommendation: body?.add_security_recommendation === true,
    },
    footer: footer
      ? {
          text: typeof footer.text === 'string' ? footer.text : '',
          codeExpirationMinutes: numberOrNull(footer.code_expiration_minutes),
        }
      : null,
    buttons: readButtons(buttons),
    // Cards, not their contents: the row says "a carousel of 4 cards", and the preview
    // component renders what it can. Claiming to render a carousel we cannot preview would be a
    // preview that lies.
    cards: Array.isArray(carousel?.cards) ? carousel.cards.length : 0,
    offer: offer
      ? {
          text: offer.limited_time_offer?.text ?? '',
          hasExpiration: offer.limited_time_offer?.has_expiration === true,
        }
      : null,
  };
};

/**
 * How many blanks the message has, counted from the text in front of us — which is a different
 * act from deriving a status nobody told us. Format-aware: a NAMED template's blanks are words,
 * a positional one's are numbers, and the same `{{…}}` scan finds both.
 */
export const countBlanks = template => {
  const { body } = readComponents(template);
  const found = body.text.match(/\{\{\s*[^}]*?\s*\}\}/g) || [];
  const seen = new Set(found.map(one => one.replace(/[{}]/g, '').trim()));
  return seen.size;
};

/** The language codes present in a library, for the language filter. */
export const languagesOf = templates =>
  [
    ...new Set(
      (templates ?? [])
        .map(template => template.language)
        .filter(language => typeof language === 'string' && language !== '')
    ),
  ].sort();

/**
 * One blocker, as a phrase an owner reads — so every refusal sentence can NAME the part rather
 * than saying "a part". Shared by the row and the editor, because two screens naming the same
 * thing differently is how an owner ends up thinking they are two problems.
 *
 * A value we have no word for falls back to Meta's own, which is the rule everywhere else here.
 */
export const blockerPart = (blocker, t) => {
  const direct = {
    MEDIA_HEADER: `WASLO.TEMPLATES.FACTS.HEADER.${blocker.detail}`,
    BUTTON: `WASLO.TEMPLATES.BUTTON_TYPE.${blocker.detail}`,
  }[blocker.reason];
  const key = direct ?? `WASLO.TEMPLATES.EDIT_BLOCKED.PARTS.${blocker.reason}`;
  const sentence = t(key);
  if (sentence !== key) return sentence;
  return blocker.detail || blocker.reason;
};
